/**
 * App-wide ephemeral toasts (MUI Snackbar + Alert).
 * Use for non-blocking success/info/warning feedback.
 * Keep AppFormAlert for blocking validation failures that need an OK dismiss.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Alert, Snackbar } from "@mui/material";

export type ToastSeverity = "success" | "info" | "warning" | "error";

export type ToastInput = {
  message: string;
  severity?: ToastSeverity;
  /** Auto-hide ms; default 4000. Errors default to 6000. */
  durationMs?: number;
};

type ToastState = ToastInput & { open: boolean; key: number };

type ToastContextValue = {
  toast: (input: ToastInput | string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ToastState>({
    open: false,
    message: "",
    severity: "info",
    key: 0,
  });

  const toast = useCallback((input: ToastInput | string) => {
    const next = typeof input === "string" ? { message: input } : input;
    setState((s) => ({
      open: true,
      message: next.message,
      severity: next.severity ?? "info",
      durationMs: next.durationMs,
      key: s.key + 1,
    }));
  }, []);

  const api = useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (message) => toast({ message, severity: "success" }),
      error: (message) => toast({ message, severity: "error", durationMs: 6000 }),
      info: (message) => toast({ message, severity: "info" }),
      warning: (message) => toast({ message, severity: "warning" }),
    }),
    [toast],
  );

  const duration =
    state.durationMs
    ?? (state.severity === "error" ? 6000 : 4000);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={state.key}
        open={state.open}
        autoHideDuration={duration}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setState((s) => ({ ...s, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={state.severity ?? "info"}
          variant="filled"
          onClose={() => setState((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
          role="status"
        >
          {state.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}
